import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import Box from 'common/components/Box';
import Text from 'common/components/Text';
import Heading from 'common/components/Heading';
import Logo from 'common/components/UIElements/Logo';
import Container from 'common/components/UI/Container';
import FooterWrapper, { List, ListItem } from './footer.style';
import { openSettings } from 'common/consent';

import LogoImage from 'common/assets/image/saasModern/CloudPouchLogoNegative.png';

const TRADEMARK_NOTE =
  'Amazon Web Services, AWS and the names of AWS services are trademarks of Amazon.com, Inc. or its affiliates. CloudPouch is not affiliated with or endorsed by AWS.';

const noteStyle = {
  color: '#c5d3ea',
  fontSize: '13px',
  lineHeight: '1.6',
  mt: '8px',
  mb: '0',
  textAlign: 'center',
};

const Footer = ({
  row,
  col,
  colOne,
  colTwo,
  titleStyle,
  logoStyle,
  textStyle,
}) => {
  const FOOTER_WIDGET = [
    {
      title: 'Contact Us',
      menuItems: [
        {
          url: 'mailto:hello@cloudpouch.dev',
          text: 'hello@cloudpouch.dev',
        },
      ],
    },
    {
      title: 'Product',
      menuItems: [
        {
          url: 'https://github.com/CloudPouch/CloudPouch.dev/releases',
          text: 'Download from GitHub',
        },
        {
          url: 'https://docs.cloudpouch.dev/',
          text: 'Documentation',
        },
      ],
    },
    {
      title: 'Social Media',
      menuItems: [
        {
          url: 'https://www.linkedin.com/in/pawelzubkiewicz/',
          text: 'LinkedIn — Paweł Zubkiewicz',
        },
        {
          url: 'https://www.linkedin.com/groups/12588087/',
          text: 'LinkedIn Group',
        },
        {
          url: 'https://twitter.com/CloudPouch',
          text: 'X',
        },
        {
          url: 'https://github.com/CloudPouch/CloudPouch.dev',
          text: 'GitHub',
        },
      ],
    },
    {
      title: 'Policy',
      menuItems: [
        {
          url: '/eula/',
          text: 'License agreement (EULA)',
        },
        {
          url: '/privacy/',
          text: 'Privacy policy',
        },
        {
          // Opens the cookie choice again (src/common/consent.js).
          action: openSettings,
          text: 'Cookie settings',
        },
      ],
    },
  ];

  return (
    <FooterWrapper id="footer">
      <Container className="footer_container">
        <Box className="row" {...row}>
          <Box {...colOne}>
            <Logo
              href="/"
              logoSrc={LogoImage}
              title="CloudPouch"
              logoStyle={logoStyle}
            />
            <Text
              content={`CloudPouch © ${new Date().getFullYear()}`}
              {...textStyle}
            />
            <Text
              content="Paweł Zubkiewicz Consulting, Wrocław, Poland"
              {...textStyle}
            />
          </Box>
          {/* End of footer logo column */}
          <Box {...colTwo}>
            {FOOTER_WIDGET.map((widget, index) => (
              <Box className="col" {...col} key={`footer-widget-${index}`}>
                <Heading content={widget.title} {...titleStyle} />
                <List>
                  {widget.menuItems.map((item, index) => (
                    <ListItem key={`footer-list-item-${index}`}>
                      {item.action ? (
                        <button
                          type="button"
                          className="ListItem"
                          onClick={item.action}
                        >
                          {item.text}
                        </button>
                      ) : (
                        <a href={item.url} className="ListItem">
                          {item.text}
                        </a>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
          {/* End of footer List column */}
        </Box>
        <Text content={TRADEMARK_NOTE} {...noteStyle} />
      </Container>
    </FooterWrapper>
  );
};

// Footer style props
Footer.propTypes = {
  row: PropTypes.object,
  col: PropTypes.object,
  colOne: PropTypes.object,
  colTwo: PropTypes.object,
  titleStyle: PropTypes.object,
  textStyle: PropTypes.object,
  logoStyle: PropTypes.object,
};

// Footer default style
Footer.defaultProps = {
  // Footer row default style
  row: {
    flexBox: true,
    flexWrap: 'wrap',
    ml: '-15px',
    mr: '-15px',
  },
  // Footer col one style
  colOne: {
    width: [1, '35%', '35%', '23%'],
    mt: [0, '13px'],
    mb: ['30px', 0],
    pl: ['15px', 0],
    pr: ['15px', '15px', 0],
  },
  // Footer col two style
  colTwo: {
    width: ['100%', '65%', '65%', '77%'],
    flexBox: true,
    flexWrap: 'wrap',
  },
  // Footer col default style
  col: {
    width: ['100%', '50%', '50%', '25%'],
    pl: '15px',
    pr: '15px',
    mb: '30px',
  },
  // widget title default style
  titleStyle: {
    color: '#fff',
    fontSize: '16px',
    fontWeight: '700',
    mb: '30px',
  },
  // Default logo size
  logoStyle: {
    width: '256px',
    mb: '15px',
  },
  // widget text default style
  textStyle: {
    color: '#c5d3ea',
    fontSize: '16px',
    mb: '10px',
    textAlign: 'center'
  },
};

export default Footer;
